import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, CheckCircle, Send } from 'lucide-react';

const API = 'https://crm.ashbournesystems.ca/api';

const DOC_TYPES = [
  'Project Agreement', 'Change Order', 'Website Handoff Receipt',
  'Maintenance Plan Agreement', 'Client Onboarding Form', 'Testimonial Release',
  'NDA', 'Late Payment Notice — 14 Day', 'Late Payment Notice — 28 Day',
  'Late Payment Notice — 56 Day', 'Subcontractor Agreement', 'Invoice'
];

export default function Documents({ token }) {
  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ client_id: '', document_type: 'Project Agreement', document_number: '', sent_to: '', notes: '' });
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    axios.get(`${API}/documents`, { headers }).then(r => setDocuments(r.data)).catch(() => {});
    axios.get(`${API}/clients`, { headers }).then(r => setClients(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/documents`, form, { headers });
      toast.success('Document logged');
      setShowForm(false);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const markSent = async (id, sent_to) => {
    try {
      await axios.put(`${API}/documents/${id}/sent`, { sent_to }, { headers });
      toast.success('Marked as sent');
      load();
    } catch { toast.error('Error'); }
  };

  const markSigned = async (id) => {
    try {
      await axios.put(`${API}/documents/${id}/signed`, {}, { headers });
      toast.success('Marked as signed');
      load();
    } catch { toast.error('Error'); }
  };

  const statusColor = { generated: '#C8941A', sent: '#4F7FA6', signed: '#4a9a4a' };

  const inputStyle = { width: '100%', padding: '0.65rem', background: '#0e0804', border: '1px solid #2a1a0e', color: '#F5ECD7', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box', marginBottom: '0.75rem' };
  const labelStyle = { display: 'block', fontSize: '0.62rem', color: '#8a6840', letterSpacing: '0.1em', marginBottom: '4px' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#F5ECD7', marginBottom: '4px' }}>Documents</h1>
          <p style={{ color: '#8a6840', fontSize: '0.85rem' }}>{documents.filter(d => d.status === 'sent').length} awaiting signature</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#B85A22', border: 'none', color: '#F5ECD7', padding: '0.75rem 1.25rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 'bold' }}>
          <Plus size={16} /> Log Document
        </button>
      </div>

      {showForm && (
        <div style={{ background: '#1a0f08', border: '1px solid #2a1a0e', borderTop: '3px solid #B85A22', padding: '1.5rem', marginBottom: '2rem' }}>
          <h3 style={{ color: '#F5ECD7', marginBottom: '1.5rem', fontSize: '1rem' }}>Log Document</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div><label style={labelStyle}>CLIENT *</label>
                <select style={inputStyle} value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} required>
                  <option value="">Select client...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.business_name || c.name}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>DOCUMENT TYPE *</label>
                <select style={inputStyle} value={form.document_type} onChange={e => setForm({...form, document_type: e.target.value})}>
                  {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>DOCUMENT NUMBER</label><input style={inputStyle} value={form.document_number} placeholder="e.g. AD-2025-001" onChange={e => setForm({...form, document_number: e.target.value})} /></div>
              <div><label style={labelStyle}>SENT TO (EMAIL)</label><input style={inputStyle} type="email" value={form.sent_to} onChange={e => setForm({...form, sent_to: e.target.value})} /></div>
            </div>
            <div><label style={labelStyle}>NOTES</label><input style={inputStyle} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="submit" style={{ background: '#B85A22', border: 'none', color: '#F5ECD7', padding: '0.75rem 1.5rem', cursor: 'pointer', fontWeight: 'bold' }}>Log Document</button>
              <button type="button" onClick={() => setShowForm(false)} style={{ background: 'transparent', border: '1px solid #2a1a0e', color: '#8a6840', padding: '0.75rem 1.5rem', cursor: 'pointer' }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div style={{ background: '#1a0f08', border: '1px solid #2a1a0e' }}>
        {documents.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#8a6840' }}>No documents logged yet</div>
        ) : documents.map((d, i) => (
          <div key={d.id} style={{ padding: '1rem 1.5rem', borderBottom: i < documents.length - 1 ? '1px solid #2a1a0e' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ color: '#F5ECD7', fontWeight: 'bold', fontSize: '0.88rem' }}>{d.document_type}</div>
              <div style={{ color: '#8a6840', fontSize: '0.75rem' }}>{d.business_name || d.client_name}</div>
              {d.sent_to && <div style={{ color: '#8a6840', fontSize: '0.72rem' }}>Sent to: {d.sent_to}</div>}
              <div style={{ color: '#8a6840', fontSize: '0.7rem' }}>{new Date(d.created_at).toLocaleDateString()}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '0.62rem', color: statusColor[d.status] || '#8a6840', border: `1px solid ${statusColor[d.status] || '#2a1a0e'}`, padding: '2px 8px' }}>
                {d.status?.toUpperCase()}
              </span>
              {d.status === 'generated' && (
                <button onClick={() => markSent(d.id, d.sent_to)} style={{ background: 'transparent', border: '1px solid #4F7FA6', color: '#4F7FA6', padding: '0.35rem 0.6rem', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Send size={11} /> Sent
                </button>
              )}
              {d.status === 'sent' && (
                <button onClick={() => markSigned(d.id)} style={{ background: 'transparent', border: '1px solid #4a9a4a', color: '#4a9a4a', padding: '0.35rem 0.6rem', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <CheckCircle size={11} /> Signed
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}